/**
 * Timer Deals — timed flash-style offers with product mapping.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { FaClock, FaTrash, FaSearch, FaTimes, FaCheck, FaBoxes, FaPlus } from "react-icons/fa";
import { FiEdit2 } from "react-icons/fi";
import {
    getTimerDeals,
    getProductsForTimerDeal,
    createTimerDeal,
    updateTimerDealMeta,
    addProductToTimerDeal,
    updateTimerDealProduct,
    removeProductFromTimerDeal,
    deleteTimerDeal,
    toggleTimerDealSlideUpView,
    toggleTimerDealAutoScroll,
} from "../../services/ClientService";
import { toast } from "react-toastify";
import { getOptionLabel, getOptionPriceHints } from "../../utils/dealOptionDisplay";

const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const h = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(h);
    }, [value, delay]);
    return debouncedValue;
};

const getProductPreview = (item) => {
    if (!item) return null;
    if (item.preview) return item.preview;
    if (item.productImage?.preview) return item.productImage.preview;
    if (item.images?.preview) return item.images.preview;
    if (Array.isArray(item.images) && item.images[0]?.preview) return item.images[0].preview;
    return null;
};

const formatExpiry = (expiresAt) => {
    if (!expiresAt) return "—";
    const d = new Date(expiresAt);
    return d.toLocaleString();
};

const isDealExpired = (deal) => {
    if (!deal?.expiresAt) return false;
    return Date.now() >= new Date(deal.expiresAt).getTime();
};

const isSlideUpActive = (deal) => !!deal?.showInSlideUpView && !isDealExpired(deal);

const dealIdsMatch = (a, b) => String(a ?? "") === String(b ?? "");

const TimerDeals = () => {
    const [deals, setDeals] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [activeDealId, setActiveDealId] = useState(null);

    const [dealName, setDealName] = useState("");
    const [durationDays, setDurationDays] = useState("0");
    const [durationHours, setDurationHours] = useState("0");
    const [durationMinutes, setDurationMinutes] = useState("30");
    const [singleProductMode, setSingleProductMode] = useState(false);
    const [isCreatingDeal, setIsCreatingDeal] = useState(false);

    const [productSearch, setProductSearch] = useState("");
    const debouncedProductSearch = useDebounce(productSearch, 500);
    const [availableProducts, setAvailableProducts] = useState([]);
    const [productsSkip, setProductsSkip] = useState(0);
    const [productsLimit] = useState(10);
    const [hasMoreProducts, setHasMoreProducts] = useState(true);
    const [isLoadingProducts, setIsLoadingProducts] = useState(false);
    const [isFetchingMore, setIsFetchingMore] = useState(false);

    const [configModalOpen, setConfigModalOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedOption, setSelectedOption] = useState(null);
    const [editingProduct, setEditingProduct] = useState(null);
    const [discountPrice, setDiscountPrice] = useState("");
    const [minimumCartValue, setMinimumCartValue] = useState("");
    const [maxQty, setMaxQty] = useState("1");
    const [modalSingleProductMode, setModalSingleProductMode] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [deleteDealConfirm, setDeleteDealConfirm] = useState(null);
    const [metaEditDeal, setMetaEditDeal] = useState(null);
    const [editDealName, setEditDealName] = useState("");
    const [editDurationDays, setEditDurationDays] = useState("0");
    const [editDurationHours, setEditDurationHours] = useState("0");
    const [editDurationMinutes, setEditDurationMinutes] = useState("0");
    const [isSavingMeta, setIsSavingMeta] = useState(false);
    const [slideUpToggleLoadingId, setSlideUpToggleLoadingId] = useState(null);
    const [autoScrollToggleLoadingId, setAutoScrollToggleLoadingId] = useState(null);
    const [scrollMsDrafts, setScrollMsDrafts] = useState({});
    const DEFAULT_SCROLL_MS = 3000;

    const hasOtherActiveSlideUp = useCallback(
        (dealId) => deals.some((d) => !dealIdsMatch(d._id, dealId) && isSlideUpActive(d)),
        [deals]
    );

    const activeDeal = useMemo(
        () => deals.find((d) => String(d._id) === String(activeDealId)),
        [deals, activeDealId]
    );

    const excludeKeys = useMemo(
        () => (activeDeal?.products || []).map((p) => `${p.productId}_${p.selectedWeight}`),
        [activeDeal]
    );

    /** Options already mapped on other timer deals — one product option per deal only */
    const otherDealsExcludeKeys = useMemo(() => {
        const keys = [];
        deals.forEach((deal) => {
            if (String(deal._id) === String(activeDealId)) return;
            (deal.products || []).forEach((p) => {
                keys.push(`${p.productId}_${p.selectedWeight}`);
            });
        });
        return keys;
    }, [deals, activeDealId]);

    const excludeKeysKey = useMemo(
        () => [...excludeKeys, ...otherDealsExcludeKeys].join("|"),
        [excludeKeys, otherDealsExcludeKeys]
    );

    const fetchDeals = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await getTimerDeals({});
            if (res.success) {
                setDeals(res.data || []);
                if (!activeDealId && res.data?.length) setActiveDealId(res.data[0]._id);
            }
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { fetchDeals(); }, [fetchDeals]);

    const [expiryTick, setExpiryTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setExpiryTick((t) => t + 1), 30000);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        if (deals.some((d) => d.showInSlideUpView && isDealExpired(d))) {
            fetchDeals();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- re-sync slide-up when timer tick detects expiry
    }, [expiryTick, fetchDeals]);

    const editingProductRef = useRef(editingProduct);
    editingProductRef.current = editingProduct;

    const excludeKeysRef = useRef(excludeKeys);
    excludeKeysRef.current = excludeKeys;

    const fetchProducts = useCallback(async (skip = 0) => {
        if (!activeDealId) return;
        if (skip === 0) {
            setIsLoadingProducts(true);
            setHasMoreProducts(true);
        } else setIsFetchingMore(true);
        try {
            const currentExclude = excludeKeysRef.current;
            const editing = editingProductRef.current;
            const keysForRequest = editing
                ? currentExclude.filter((k) => k !== `${editing.productId}_${editing.selectedWeight}`)
                : currentExclude;

            const res = await getProductsForTimerDeal({
                searchTerm: debouncedProductSearch,
                excludeKeys: keysForRequest,
                timerDealId: activeDealId,
                skip,
                limit: productsLimit,
            });
            if (res.success) {
                const fetched = res.data || [];
                if (skip === 0) setAvailableProducts(fetched);
                else setAvailableProducts((prev) => [...prev, ...fetched]);
                setHasMoreProducts(fetched.length === productsLimit);
                setProductsSkip(skip + fetched.length);
            }
        } finally {
            setIsLoadingProducts(false);
            setIsFetchingMore(false);
        }
    }, [activeDealId, debouncedProductSearch, productsLimit]);

    useEffect(() => {
        if (!activeDealId) {
            setAvailableProducts([]);
            return;
        }
        setProductsSkip(0);
        fetchProducts(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchProducts uses refs; deps are stable keys only
    }, [activeDealId, debouncedProductSearch, excludeKeysKey]);

    const handleCreateDeal = async () => {
        if (!dealName.trim()) {
            toast.error("Deal name is required");
            return;
        }
        const total = (Number(durationDays) || 0) * 86400
            + (Number(durationHours) || 0) * 3600
            + (Number(durationMinutes) || 0) * 60;
        if (total <= 0) {
            toast.error("Set at least one of days, hours, or minutes");
            return;
        }
        setIsCreatingDeal(true);
        try {
            const res = await createTimerDeal({
                dealName: dealName.trim(),
                durationDays: Number(durationDays) || 0,
                durationHours: Number(durationHours) || 0,
                durationMinutes: Number(durationMinutes) || 0,
                singleProductMode,
            });
            if (res.success) {
                toast.success("Timer deal created");
                setDealName("");
                setActiveDealId(res.data._id);
                await fetchDeals();
            } else toast.error(res.message || "Failed to create deal");
        } finally {
            setIsCreatingDeal(false);
        }
    };

    const openConfigModal = (product, option, existing = null) => {
        if (!activeDealId) {
            toast.error("Create or select a timer deal first");
            return;
        }
        if (!existing && isDealExpired(activeDeal)) {
            toast.error("This timer deal has expired. Extend the duration via Edit to add more products.");
            return;
        }
        setSelectedProduct(product);
        setSelectedOption(option);
        setEditingProduct(existing);
        setDiscountPrice(existing ? String(existing.discountPrice) : "");
        setMinimumCartValue(existing ? String(existing.minimumCartValue ?? 0) : "");
        setMaxQty(existing ? String(existing.maxAllowedQuantityInOneOrder ?? 1) : "1");
        setModalSingleProductMode(activeDeal?.singleProductMode ?? false);
        setConfigModalOpen(true);
    };

    const validateForm = () => {
        const flash = Number(discountPrice);
        const regular = Number(selectedOption?.discountPrice);
        if (!Number.isFinite(flash) || flash <= 0) {
            toast.error("Discount price must be greater than 0");
            return false;
        }
        if (Number.isFinite(regular) && flash >= regular) {
            toast.error("Discount price should be lower than regular discount price");
            return false;
        }
        if (!Number.isFinite(Number(minimumCartValue)) || Number(minimumCartValue) < 0) {
            toast.error("Minimum cart value must be 0 or greater");
            return false;
        }
        if (!Number.isFinite(Number(maxQty)) || Number(maxQty) < 1) {
            toast.error("Max quantity must be at least 1");
            return false;
        }
        return true;
    };

    const handleSaveProduct = async () => {
        if (!validateForm()) return;
        if (!editingProduct && isDealExpired(activeDeal)) {
            toast.error("This timer deal has expired. Extend the duration via Edit to add more products.");
            return;
        }
        setIsSaving(true);
        try {
            const payload = {
                timerDealId: activeDealId,
                productId: selectedProduct._id,
                selectedWeight: getOptionLabel(selectedOption),
                discountPrice: Number(discountPrice),
                minimumCartValue: Number(minimumCartValue || 0),
                maxAllowedQuantityInOneOrder: Number(maxQty || 1),
            };
            if (modalSingleProductMode !== !!activeDeal?.singleProductMode) {
                await updateTimerDealMeta({
                    _id: activeDealId,
                    singleProductMode: modalSingleProductMode,
                });
            }
            const res = editingProduct
                ? await updateTimerDealProduct(payload)
                : await addProductToTimerDeal(payload);
            if (res.success) {
                toast.success(editingProduct ? "Updated" : "Product added to timer deal");
                setConfigModalOpen(false);
                fetchDeals();
            } else if (res.errorType === "TIMER_DEAL_OPTION_ALREADY_MAPPED") {
                toast.error(
                    res.message
                        || `This option is already in "${res.conflictDealName || "another timer deal"}". Each option can only belong to one timer deal.`
                );
            } else toast.error(res.message || "Failed to save");
        } catch (err) {
            const msg = err?.errorData?.message || err?.message || "Failed to save";
            toast.error(msg);
        } finally {
            setIsSaving(false);
        }
    };

    const handleRemoveProduct = async (p) => {
        const res = await removeProductFromTimerDeal({
            timerDealId: activeDealId,
            productId: p.productId,
            selectedWeight: p.selectedWeight,
        });
        if (res.success) {
            toast.success("Product removed — prices restored");
            fetchDeals();
        } else toast.error(res.message || "Failed to remove");
    };

    const openMetaEditModal = (deal, e) => {
        e?.stopPropagation?.();
        setMetaEditDeal(deal);
        setEditDealName(deal.dealName || "");
        setEditDurationDays(String(deal.durationDays ?? 0));
        setEditDurationHours(String(deal.durationHours ?? 0));
        setEditDurationMinutes(String(deal.durationMinutes ?? 0));
    };

    const handleSaveDealMeta = async () => {
        if (!metaEditDeal) return;
        if (!editDealName.trim()) {
            toast.error("Deal name is required");
            return;
        }
        const total = (Number(editDurationDays) || 0) * 86400
            + (Number(editDurationHours) || 0) * 3600
            + (Number(editDurationMinutes) || 0) * 60;
        if (total <= 0) {
            toast.error("Set at least one of days, hours, or minutes");
            return;
        }
        setIsSavingMeta(true);
        try {
            const res = await updateTimerDealMeta({
                _id: metaEditDeal._id,
                dealName: editDealName.trim(),
                durationDays: Number(editDurationDays) || 0,
                durationHours: Number(editDurationHours) || 0,
                durationMinutes: Number(editDurationMinutes) || 0,
            });
            if (res.success) {
                toast.success("Timer deal updated");
                setMetaEditDeal(null);
                await fetchDeals();
            } else toast.error(res.message || "Failed to update deal");
        } finally {
            setIsSavingMeta(false);
        }
    };

    const handleDeleteDeal = async (id) => {
        const res = await deleteTimerDeal({ _id: id });
        if (res.success) {
            toast.success("Timer deal deleted — all products restored");
            setDeleteDealConfirm(null);
            if (String(activeDealId) === String(id)) setActiveDealId(null);
            fetchDeals();
        } else toast.error(res.message || "Failed to delete");
    };

    const handleToggleAutoScroll = async (deal, e) => {
        e?.stopPropagation?.();
        if (autoScrollToggleLoadingId) return;
        const nextValue = !deal.enableAutoScroll;
        if (nextValue && !(deal.products || []).length) {
            toast.error("Add at least one product before enabling auto scroll.");
            return;
        }
        const draftMs = scrollMsDrafts[deal._id];
        const intervalMs = draftMs !== undefined && draftMs !== ""
            ? Number(draftMs)
            : (deal.autoScrollIntervalMs || DEFAULT_SCROLL_MS);

        setAutoScrollToggleLoadingId(deal._id);
        try {
            const res = await toggleTimerDealAutoScroll({
                timerDealId: deal._id,
                enableAutoScroll: nextValue,
                autoScrollIntervalMs: intervalMs,
            });
            if (res.success) {
                toast.success(
                    nextValue
                        ? "Auto scroll enabled on Home and slide-up for this deal"
                        : "Auto scroll disabled for this deal"
                );
                await fetchDeals();
            } else {
                toast.error(res.message || "Failed to update auto scroll");
            }
        } catch (err) {
            toast.error(err?.message || "Failed to update auto scroll");
        } finally {
            setAutoScrollToggleLoadingId(null);
        }
    };

    const handleSaveScrollMs = async (deal, e) => {
        e?.stopPropagation?.();
        const draftMs = scrollMsDrafts[deal._id];
        const intervalMs = Number(draftMs ?? deal.autoScrollIntervalMs ?? DEFAULT_SCROLL_MS);
        if (!Number.isFinite(intervalMs) || intervalMs < 500) {
            toast.error("Scroll time must be at least 500 milliseconds");
            return;
        }
        setAutoScrollToggleLoadingId(deal._id);
        try {
            const res = await toggleTimerDealAutoScroll({
                timerDealId: deal._id,
                enableAutoScroll: !!deal.enableAutoScroll,
                autoScrollIntervalMs: intervalMs,
            });
            if (res.success) {
                toast.success("Scroll time updated");
                await fetchDeals();
            } else toast.error(res.message || "Failed to update scroll time");
        } catch (err) {
            toast.error(err?.message || "Failed to update scroll time");
        } finally {
            setAutoScrollToggleLoadingId(null);
        }
    };

    const handleToggleSlideUpView = async (deal, e) => {
        e?.stopPropagation?.();
        if (slideUpToggleLoadingId) return;

        const currentlyActive = isSlideUpActive(deal);
        const nextValue = !currentlyActive;

        if (nextValue && isDealExpired(deal)) {
            toast.error("Cannot enable slide-up for an expired deal. Extend the duration first.");
            return;
        }
        if (nextValue && !(deal.products || []).length) {
            toast.error("Add at least one product before enabling the slide-up view.");
            return;
        }
        if (nextValue && hasOtherActiveSlideUp(deal._id)) {
            toast.error("Turn off slide-up on the other timer deal first.");
            return;
        }

        setSlideUpToggleLoadingId(deal._id);
        setDeals((prev) =>
            prev.map((d) => {
                if (dealIdsMatch(d._id, deal._id)) {
                    return { ...d, showInSlideUpView: nextValue };
                }
                if (nextValue) {
                    return { ...d, showInSlideUpView: false };
                }
                return d;
            })
        );

        try {
            const res = await toggleTimerDealSlideUpView({
                timerDealId: deal._id,
                showInSlideUpView: nextValue,
            });
            if (res.success) {
                toast.success(
                    nextValue
                        ? "Slide-up view enabled — customers will see this deal when they open the app or refresh the website"
                        : "Slide-up view disabled"
                );
                await fetchDeals();
            } else {
                toast.error(res.message || "Failed to update slide-up setting");
                await fetchDeals();
            }
        } catch (err) {
            toast.error(err?.message || "Failed to update slide-up setting");
            await fetchDeals();
        } finally {
            setSlideUpToggleLoadingId(null);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="mb-6 flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
                    <FaClock className="text-white text-2xl" />
                </div>
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Timer Deals</h1>
                    <p className="text-gray-500 text-sm">Time-limited offers with the same cart rules as Flash Deals</p>
                </div>
            </div>

            <div className="bg-white rounded-xl border border-amber-100 p-4 mb-6 shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-3">New Timer Deal</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
                    <div className="lg:col-span-2">
                        <label className="text-xs font-medium text-gray-600">Deal Name *</label>
                        <input value={dealName} onChange={(e) => setDealName(e.target.value)} className="w-full mt-1 border rounded-lg px-3 py-2" placeholder="Weekend Blast" />
                    </div>
                    <div>
                        <label className="text-xs font-medium text-gray-600">Days</label>
                        <input type="number" min={0} value={durationDays} onChange={(e) => setDurationDays(e.target.value)} className="w-full mt-1 border rounded-lg px-3 py-2" />
                    </div>
                    <div>
                        <label className="text-xs font-medium text-gray-600">Hours</label>
                        <input type="number" min={0} value={durationHours} onChange={(e) => setDurationHours(e.target.value)} className="w-full mt-1 border rounded-lg px-3 py-2" />
                    </div>
                    <div>
                        <label className="text-xs font-medium text-gray-600">Minutes</label>
                        <input type="number" min={0} value={durationMinutes} onChange={(e) => setDurationMinutes(e.target.value)} className="w-full mt-1 border rounded-lg px-3 py-2" />
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-4 mt-3">
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={singleProductMode} onChange={(e) => setSingleProductMode(e.target.checked)} />
                        Only one timer deal item per cart
                    </label>
                    <button type="button" onClick={handleCreateDeal} disabled={isCreatingDeal} className="px-5 py-2 bg-amber-600 text-white font-semibold rounded-lg flex items-center gap-2">
                        <FaPlus /> {isCreatingDeal ? "Creating..." : "Create Deal"}
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                    <h3 className="font-semibold mb-2">Add Products to Deal</h3>
                    {!activeDealId ? (
                        <p className="text-gray-500 text-sm py-8 text-center">Select a deal on the right or create one</p>
                    ) : (
                        <>
                            <p className="text-xs text-amber-700 mb-3 font-medium">
                                Active: {activeDeal?.dealName} · Ends {formatExpiry(activeDeal?.expiresAt)}
                                {isDealExpired(activeDeal) && (
                                    <span className="ml-2 inline-flex px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold">Expired</span>
                                )}
                            </p>
                            <div className="relative mb-3">
                                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input type="text" placeholder="Search products..." value={productSearch} onChange={(e) => setProductSearch(e.target.value)} className="w-full pl-10 pr-4 py-2 border rounded-lg" />
                            </div>
                            <div
                                className="h-[360px] overflow-auto border rounded-lg"
                                onScroll={(e) => {
                                    const el = e.currentTarget;
                                    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50 && !isFetchingMore && hasMoreProducts) fetchProducts(productsSkip);
                                }}
                            >
                                {isLoadingProducts && productsSkip === 0 ? (
                                    <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-t-2 border-amber-500 rounded-full" /></div>
                                ) : (
                                    <div className="divide-y">
                                        {availableProducts.map((product) => (
                                            <div key={product._id} className="p-3">
                                                <p className="font-medium text-sm">{product.productName}</p>
                                                <div className="flex flex-wrap gap-2 mt-2">
                                                    {(product.productOptions || []).map((opt, i) => {
                                                        const hints = getOptionPriceHints(opt);
                                                        return (
                                                        <button
                                                            key={i}
                                                            type="button"
                                                            disabled={isDealExpired(activeDeal)}
                                                            onClick={() => openConfigModal(product, opt)}
                                                            className={`px-2 py-1.5 text-left text-xs rounded-lg border ${
                                                                isDealExpired(activeDeal)
                                                                    ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                                                                    : "bg-amber-50 text-amber-900 border-amber-200 hover:bg-amber-100"
                                                            }`}
                                                        >
                                                            <span className="font-semibold block">{getOptionLabel(opt)}</span>
                                                            {(hints.regularLabel || hints.pctLabel) && (
                                                                <span className="text-[10px] text-amber-700">{hints.regularLabel}{hints.pctLabel ? ` · ${hints.pctLabel}` : ""}</span>
                                                            )}
                                                        </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>

                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                    <h3 className="font-semibold mb-3">Your Timer Deals</h3>
                    {isLoading ? (
                        <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-t-2 border-amber-500 rounded-full" /></div>
                    ) : deals.length === 0 ? (
                        <p className="text-gray-500 text-center py-12">No timer deals yet</p>
                    ) : (
                        <div className="space-y-4 max-h-[520px] overflow-auto">
                            {deals.map((deal) => {
                                const slideUpActive = isSlideUpActive(deal);
                                const blockedByOtherSlideUp = !slideUpActive && hasOtherActiveSlideUp(deal._id);
                                return (
                                <div
                                    key={deal._id}
                                    className={`border rounded-xl p-3 cursor-pointer ${String(activeDealId) === String(deal._id) ? "border-amber-400 bg-amber-50/50" : "border-gray-100"}`}
                                    onClick={() => setActiveDealId(deal._id)}
                                >
                                    <div className="flex justify-between items-start gap-2">
                                        <div className="min-w-0 flex-1">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <p className="font-bold text-gray-900">{deal.dealName}</p>
                                                {isDealExpired(deal) && (
                                                    <span className="inline-flex px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-full bg-red-100 text-red-700 border border-red-200">
                                                        Expired
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs text-gray-500">
                                                {deal.durationDays || 0}d {deal.durationHours || 0}h {deal.durationMinutes || 0}m · Ends {formatExpiry(deal.expiresAt)}
                                            </p>
                                            <p className="text-xs text-amber-700 mt-1">{(deal.products || []).length} product(s)</p>
                                        </div>
                                        <div className="flex shrink-0">
                                            <button type="button" title="Edit name & timer" onClick={(e) => openMetaEditModal(deal, e)} className="p-2 text-gray-400 hover:text-amber-600"><FiEdit2 /></button>
                                            <button type="button" onClick={(e) => { e.stopPropagation(); setDeleteDealConfirm(deal._id); }} className="p-2 text-gray-400 hover:text-red-600"><FaTrash /></button>
                                        </div>
                                    </div>
                                    <div
                                        className={`mt-3 pt-3 border-t flex flex-wrap items-center gap-3 ${
                                            slideUpActive ? "border-amber-200 bg-amber-50/60 -mx-3 px-3 pb-3 rounded-b-lg" : "border-gray-100"
                                        }`}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <div className="flex-1 min-w-[180px]">
                                            <p className="text-xs font-semibold text-gray-800 leading-snug">
                                                Show in slide-up when customer opens app or refreshes website
                                            </p>
                                            <p className="text-[10px] text-gray-500 mt-0.5">
                                                {isDealExpired(deal)
                                                    ? "Off — deal expired; extend duration to use slide-up again"
                                                    : slideUpActive
                                                    ? "Active — only one deal can use slide-up at a time"
                                                    : blockedByOtherSlideUp
                                                        ? "Disabled — turn off slide-up on the other deal first"
                                                        : "Off — enable to greet customers with this deal"}
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            role="switch"
                                            aria-checked={slideUpActive}
                                            disabled={
                                                slideUpToggleLoadingId === deal._id
                                                || isDealExpired(deal)
                                                || (!(deal.products || []).length && !slideUpActive)
                                                || blockedByOtherSlideUp
                                            }
                                            onClick={(e) => handleToggleSlideUpView(deal, e)}
                                            title={
                                                isDealExpired(deal)
                                                    ? "Slide-up was turned off because this deal expired"
                                                    : !(deal.products || []).length
                                                        ? "Add products to enable slide-up"
                                                        : blockedByOtherSlideUp
                                                            ? "Turn off slide-up on the other timer deal first"
                                                            : undefined
                                            }
                                            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-400 ${
                                                slideUpActive ? "bg-amber-500" : "bg-gray-300"
                                            } ${
                                                slideUpToggleLoadingId === deal._id
                                                || isDealExpired(deal)
                                                || (!(deal.products || []).length && !slideUpActive)
                                                || blockedByOtherSlideUp
                                                    ? "opacity-50 cursor-not-allowed"
                                                    : "cursor-pointer"
                                            }`}
                                        >
                                            <span
                                                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-1 ring-black/5 transition-transform duration-300 ${
                                                    slideUpActive ? "translate-x-5" : "translate-x-0.5"
                                                }`}
                                            />
                                        </button>
                                    </div>
                                    <div
                                        className={`mt-3 pt-3 border-t flex flex-col gap-2 ${
                                            deal.enableAutoScroll ? "border-emerald-200 bg-emerald-50/50 -mx-3 px-3 pb-3 rounded-b-lg" : "border-gray-100"
                                        }`}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <div className="flex flex-wrap items-center gap-3">
                                            <div className="flex-1 min-w-[180px]">
                                                <p className="text-xs font-semibold text-gray-800 leading-snug">
                                                    Enable auto scroll (Home + slide-up)
                                                </p>
                                                <p className="text-[10px] text-gray-500 mt-0.5">
                                                    {deal.enableAutoScroll
                                                        ? "Products scroll automatically in a continuous loop"
                                                        : "Turn on to auto-advance deal products on customer Home and slide-up"}
                                                </p>
                                            </div>
                                            <button
                                                type="button"
                                                role="switch"
                                                aria-checked={!!deal.enableAutoScroll}
                                                disabled={
                                                    autoScrollToggleLoadingId === deal._id
                                                    || (!(deal.products || []).length && !deal.enableAutoScroll)
                                                }
                                                onClick={(e) => handleToggleAutoScroll(deal, e)}
                                                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-400 ${
                                                    deal.enableAutoScroll ? "bg-emerald-500" : "bg-gray-300"
                                                } ${
                                                    autoScrollToggleLoadingId === deal._id
                                                    || (!(deal.products || []).length && !deal.enableAutoScroll)
                                                        ? "opacity-50 cursor-not-allowed"
                                                        : "cursor-pointer"
                                                }`}
                                            >
                                                <span
                                                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-1 ring-black/5 transition-transform duration-300 ${
                                                        deal.enableAutoScroll ? "translate-x-5" : "translate-x-0.5"
                                                    }`}
                                                />
                                            </button>
                                        </div>
                                        {deal.enableAutoScroll && (
                                            <div className="flex flex-wrap items-end gap-2">
                                                <div>
                                                    <label className="text-[10px] font-medium text-gray-600">
                                                        Scroll time (milliseconds)
                                                    </label>
                                                    <input
                                                        type="number"
                                                        min={500}
                                                        step={100}
                                                        className="mt-0.5 w-36 border rounded-lg px-2 py-1.5 text-sm"
                                                        placeholder={String(DEFAULT_SCROLL_MS)}
                                                        value={
                                                            scrollMsDrafts[deal._id] !== undefined
                                                                ? scrollMsDrafts[deal._id]
                                                                : String(deal.autoScrollIntervalMs || DEFAULT_SCROLL_MS)
                                                        }
                                                        onChange={(e) => setScrollMsDrafts((prev) => ({
                                                            ...prev,
                                                            [deal._id]: e.target.value,
                                                        }))}
                                                    />
                                                    <p className="text-[9px] text-gray-400 mt-0.5">
                                                        Default {DEFAULT_SCROLL_MS} ms if left blank when enabling
                                                    </p>
                                                </div>
                                                <button
                                                    type="button"
                                                    disabled={autoScrollToggleLoadingId === deal._id}
                                                    onClick={(e) => handleSaveScrollMs(deal, e)}
                                                    className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                                                >
                                                    Save interval
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    {(deal.products || []).length > 0 && (
                                        <div className="mt-3 space-y-2 border-t pt-2">
                                            {deal.products.map((p, idx) => (
                                                <div key={idx} className="flex items-center gap-2 text-sm">
                                                    <span className="flex-1 truncate">{p.productName} · {p.selectedWeight}</span>
                                                    <span className="text-amber-700 font-medium">₹{p.discountPrice}</span>
                                                    <button type="button" onClick={(e) => { e.stopPropagation(); openConfigModal({ _id: p.productId, productName: p.productName }, { discountPrice: p.discountPrice, weight: p.selectedWeight }, p); }} className="p-1 text-gray-500"><FiEdit2 size={14} /></button>
                                                    <button type="button" onClick={(e) => { e.stopPropagation(); handleRemoveProduct(p); }} className="p-1 text-red-500"><FaTrash size={14} /></button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {configModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl w-full max-w-md p-6">
                        <div className="flex justify-between mb-4">
                            <h2 className="text-lg font-bold">{editingProduct ? "Edit" : "Add"} Timer Deal Product</h2>
                            <button type="button" onClick={() => setConfigModalOpen(false)}><FaTimes /></button>
                        </div>
                        <p className="text-sm text-gray-600 mb-4">{selectedProduct?.productName} — {getOptionLabel(selectedOption)}</p>
                        <div className="space-y-3">
                            <div>
                                <label className="text-sm font-medium">Discount Price *</label>
                                <input type="number" value={discountPrice} onChange={(e) => setDiscountPrice(e.target.value)} className="w-full mt-1 border rounded-lg px-3 py-2" />
                            </div>
                            <div>
                                <label className="text-sm font-medium">Minimum Cart Value *</label>
                                <input type="number" value={minimumCartValue} onChange={(e) => setMinimumCartValue(e.target.value)} className="w-full mt-1 border rounded-lg px-3 py-2" />
                            </div>
                            <div>
                                <label className="text-sm font-medium">Max Qty In One Order *</label>
                                <input type="number" min={1} value={maxQty} onChange={(e) => setMaxQty(e.target.value)} className="w-full mt-1 border rounded-lg px-3 py-2" />
                            </div>
                            <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={modalSingleProductMode} onChange={(e) => setModalSingleProductMode(e.target.checked)} />
                                Only one timer deal item per cart
                            </label>
                        </div>
                        <div className="flex gap-3 mt-6">
                            <button type="button" onClick={() => setConfigModalOpen(false)} className="flex-1 py-2 border rounded-lg">Cancel</button>
                            <button type="button" onClick={handleSaveProduct} disabled={isSaving} className="flex-1 py-2 bg-amber-600 text-white rounded-lg font-semibold flex justify-center gap-2 items-center">
                                {isSaving ? "Saving..." : <><FaCheck /> Save</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {metaEditDeal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl w-full max-w-md p-6">
                        <div className="flex justify-between mb-4">
                            <h2 className="text-lg font-bold">Edit Timer Deal</h2>
                            <button type="button" onClick={() => setMetaEditDeal(null)}><FaTimes /></button>
                        </div>
                        <p className="text-xs text-gray-500 mb-4">Saving a new duration restarts the countdown from now.</p>
                        <div className="space-y-3">
                            <div>
                                <label className="text-sm font-medium">Deal Name *</label>
                                <input value={editDealName} onChange={(e) => setEditDealName(e.target.value)} className="w-full mt-1 border rounded-lg px-3 py-2" />
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <label className="text-xs font-medium text-gray-600">Days</label>
                                    <input type="number" min={0} value={editDurationDays} onChange={(e) => setEditDurationDays(e.target.value)} className="w-full mt-1 border rounded-lg px-3 py-2" />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-gray-600">Hours</label>
                                    <input type="number" min={0} value={editDurationHours} onChange={(e) => setEditDurationHours(e.target.value)} className="w-full mt-1 border rounded-lg px-3 py-2" />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-gray-600">Minutes</label>
                                    <input type="number" min={0} value={editDurationMinutes} onChange={(e) => setEditDurationMinutes(e.target.value)} className="w-full mt-1 border rounded-lg px-3 py-2" />
                                </div>
                            </div>
                            {isDealExpired(metaEditDeal) && (
                                <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                                    This deal has expired. Update the timer to make it active again for customers.
                                </p>
                            )}
                        </div>
                        <div className="flex gap-3 mt-6">
                            <button type="button" onClick={() => setMetaEditDeal(null)} className="flex-1 py-2 border rounded-lg">Cancel</button>
                            <button type="button" onClick={handleSaveDealMeta} disabled={isSavingMeta} className="flex-1 py-2 bg-amber-600 text-white rounded-lg font-semibold flex justify-center gap-2 items-center">
                                {isSavingMeta ? "Saving..." : <><FaCheck /> Save</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {deleteDealConfirm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl p-6 max-w-sm w-full">
                        <p className="font-semibold mb-2">Delete entire timer deal?</p>
                        <p className="text-sm text-gray-600 mb-4">All mapped products will revert to original prices.</p>
                        <div className="flex gap-3">
                            <button type="button" onClick={() => setDeleteDealConfirm(null)} className="flex-1 py-2 border rounded-lg">Cancel</button>
                            <button type="button" onClick={() => handleDeleteDeal(deleteDealConfirm)} className="flex-1 py-2 bg-red-600 text-white rounded-lg">Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TimerDeals;
